import { useMemo } from "react"
import { marked, Renderer } from "marked"
import { cn } from "../../lib/utils"

// One module-level renderer override: agent-generated links open
// in a new tab and carry the security rels. The original `/chat`
// page uses the same override.
const renderer = new Renderer()
renderer.link = ({ href, text }) =>
  `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
marked.use({ renderer })

interface MarkdownProps {
  text: string
  className?: string
}

/**
 * Renders agent text as rendered markdown. Used for assistant
 * bubbles only — user messages are kept as plain text since the
 * user typed them raw and shouldn't have surprise formatting.
 *
 * No sanitisation layer (no DOMPurify) for now: the source is our
 * own agents talking to our own users. If we ever surface
 * user-authored content here we should add sanitisation.
 *
 * Styling: hand-tuned Tailwind utilities for the common markdown
 * elements (paragraphs, lists, headings, code, tables) so the
 * output blends with the chat aesthetic without pulling in
 * `@tailwindcss/typography`.
 */
export function Markdown({ text, className }: MarkdownProps) {
  const html = useMemo(() => marked.parse(text) as string, [text])
  return (
    <div
      data-slot="agent-chat-markdown"
      className={cn(
        "prose-tight",
        // Paragraph spacing
        "[&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
        // Lists
        "[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5",
        "[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5",
        "[&_li]:my-0.5 [&_li>p]:my-0",
        // Headings
        "[&_h1]:font-heading [&_h1]:text-base [&_h1]:font-medium [&_h1]:mt-3 [&_h1]:mb-1.5",
        "[&_h2]:font-heading [&_h2]:text-[0.95rem] [&_h2]:font-medium [&_h2]:mt-3 [&_h2]:mb-1.5",
        "[&_h3]:font-heading [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-2 [&_h3]:mb-1",
        // Inline code
        "[&_code]:rounded [&_code]:bg-foreground/[0.08] [&_code]:px-1 [&_code]:py-px [&_code]:text-[0.85em] [&_code]:font-mono",
        // Code blocks
        "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-foreground/[0.05] [&_pre]:p-2.5 [&_pre]:text-[0.8rem]",
        "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
        // Blockquote
        "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-foreground/20 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
        // Links
        "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:opacity-80",
        // Tables
        "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[0.85em]",
        "[&_th]:border-b [&_th]:border-foreground/15 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-medium",
        "[&_td]:border-b [&_td]:border-foreground/10 [&_td]:px-2 [&_td]:py-1",
        // HR
        "[&_hr]:my-3 [&_hr]:border-foreground/15",
        // Strong / em
        "[&_strong]:font-semibold",
        "[&_em]:italic",
        className,
      )}
      // Trusted source: our own agents. If we ever surface untrusted
      // content here, wrap with a sanitiser first.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
